import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Trophy, TrendingUp, Euro, Copy, Link as LinkIcon, Share2, ExternalLink,
  Crown, Medal, Eye, CheckCircle2, ArrowLeft, Phone, Mail, BadgeCheck,
  RefreshCw, Ban, Trash2, Power,
} from "lucide-react";

const rankIcons = [Crown, Medal, Trophy];
const rankColors = ["text-amber-500", "text-slate-400", "text-orange-600"];

const Parrainages = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [searchParams, setSearchParams] = useSearchParams();
  const initialParrain = searchParams.get("parrain");

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [detailId, setDetailId] = useState(initialParrain || null);
  const [detail, setDetail] = useState(null);

  const appOrigin = window.location.origin;
  const baseLinkUrl = (code) => `${appOrigin}/p/${code}`;

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/parrainage/stats`, { headers });
      setStats(r.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStats(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!detailId) { setDetail(null); return; }
    axios.get(`${API}/parrainage/parrain/${detailId}`, { headers })
      .then((r) => setDetail(r.data))
      .catch(() => toast.error("Erreur détail"));
    setSearchParams(detailId ? { parrain: detailId } : {});
  }, [detailId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredLeaderboard = useMemo(() => {
    if (!stats) return [];
    const q = search.trim().toLowerCase();
    if (!q) return stats.leaderboard;
    return stats.leaderboard.filter((p) =>
      (p.nom || "").toLowerCase().includes(q) ||
      (p.code || "").toLowerCase().includes(q) ||
      (p.telephone || "").includes(q) ||
      (p.email || "").toLowerCase().includes(q)
    );
  }, [stats, search]);

  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(baseLinkUrl(code));
      toast.success("Lien copié !");
    } catch { toast.error("Impossible de copier"); }
  };

  const shareLink = async (p) => {
    const text = `Bonjour ${p.nom.split(" ")[0]}, voici votre lien de parrainage TopCocoon : ${baseLinkUrl(p.code)} — Partagez-le à vos proches et gagnez ${stats?.reward_per_filleul || 15}€ par filleul !`;
    // Try SMS if phone
    if (p.telephone) {
      window.location.href = `sms:${p.telephone.replace(/\s+/g, "")}?body=${encodeURIComponent(text)}`;
    } else if (p.email) {
      window.location.href = `mailto:${p.email}?subject=${encodeURIComponent("Votre lien de parrainage TopCocoon")}&body=${encodeURIComponent(text)}`;
    } else {
      copyLink(p.code);
    }
  };

  const togglePaye = async (id, current) => {
    try {
      await axios.post(`${API}/parrainage/parrain/${id}/paye`, { paye: !current }, { headers });
      toast.success(!current ? "Marqué comme payé" : "Marqué comme dû");
      fetchStats();
      if (detailId === id) {
        const r = await axios.get(`${API}/parrainage/parrain/${id}`, { headers });
        setDetail(r.data);
      }
    } catch { toast.error("Erreur"); }
  };

  const regenerate = async (id, nom) => {
    if (!window.confirm(`Régénérer le code de ${nom} ?\n\nL'ancien lien de parrainage cessera de fonctionner immédiatement.`)) return;
    try {
      const r = await axios.post(`${API}/parrainage/parrain/${id}/regenerate`, {}, { headers });
      toast.success(`Nouveau code : ${r.data.code}`);
      fetchStats();
      if (detailId === id) {
        const d = await axios.get(`${API}/parrainage/parrain/${id}`, { headers });
        setDetail(d.data);
      }
    } catch { toast.error("Erreur régénération"); }
  };

  const toggleActive = async (id, currentActif, nom) => {
    const verb = currentActif ? "désactiver" : "réactiver";
    if (!window.confirm(`${verb.charAt(0).toUpperCase()+verb.slice(1)} le parrainage de ${nom} ?`)) return;
    try {
      await axios.post(`${API}/parrainage/parrain/${id}/toggle-active`, { actif: !currentActif }, { headers });
      toast.success(currentActif ? "Parrainage désactivé" : "Parrainage réactivé");
      fetchStats();
      if (detailId === id) {
        const d = await axios.get(`${API}/parrainage/parrain/${id}`, { headers });
        setDetail(d.data);
      }
    } catch { toast.error("Erreur"); }
  };

  const deleteParrain = async (id, nom) => {
    if (!window.confirm(`Supprimer le parrainage de ${nom} ?\n\n• Le code sera effacé\n• L'historique des clics sera supprimé\n• Les filleuls déjà liés resteront intacts pour l'historique\n\nCette action est IRRÉVERSIBLE.`)) return;
    try {
      const r = await axios.delete(`${API}/parrainage/parrain/${id}`, { headers });
      toast.success(`Parrainage supprimé (${r.data.clics_supprimes} clics effacés)`);
      if (detailId === id) setDetailId(null);
      fetchStats();
    } catch { toast.error("Erreur suppression"); }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Chargement…</div>;
  if (!stats) return <div className="text-center py-12 text-slate-400">Aucune donnée</div>;

  return (
    <div className="space-y-6" data-testid="parrainages-page">
      {detail ? (
        // -------- Detail view --------
        <>
          <button onClick={() => setDetailId(null)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-list">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-bold text-slate-800">
                {detail.parrain.nom}
                {detail.parrain.actif === false && (
                  <span className="ml-3 text-sm font-normal text-amber-700 bg-amber-100 border border-amber-200 px-2 py-1 rounded">
                    Parrainage désactivé
                  </span>
                )}
              </h1>
              <div className="mt-1 flex items-center gap-3 text-sm text-slate-500 flex-wrap">
                {detail.parrain.telephone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{detail.parrain.telephone}</span>}
                {detail.parrain.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{detail.parrain.email}</span>}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => copyLink(detail.parrain.code)} data-testid="detail-copy-link">
                <Copy className="w-4 h-4 mr-2" /> Copier le lien
              </Button>
              <Button onClick={() => shareLink(detail.parrain)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="detail-share-link">
                <Share2 className="w-4 h-4 mr-2" /> Partager
              </Button>
              <Button variant="outline" onClick={() => regenerate(detail.parrain.id, detail.parrain.nom)} data-testid="detail-regenerate" title="Régénérer le code">
                <RefreshCw className="w-4 h-4 mr-2" /> Nouveau code
              </Button>
              <Button variant="outline" onClick={() => toggleActive(detail.parrain.id, detail.parrain.actif, detail.parrain.nom)} data-testid="detail-toggle-active" className={detail.parrain.actif === false ? "text-amber-600 border-amber-300 hover:bg-amber-50" : ""}>
                {detail.parrain.actif === false ? <><Power className="w-4 h-4 mr-2" /> Réactiver</> : <><Ban className="w-4 h-4 mr-2" /> Désactiver</>}
              </Button>
              <Button variant="outline" onClick={() => deleteParrain(detail.parrain.id, detail.parrain.nom)} data-testid="detail-delete" className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Clics sur le lien" value={detail.clics.length} icon={LinkIcon} color="indigo" />
                <Stat label="Filleuls convertis" value={detail.filleuls.length} icon={Users} color="emerald" />
                <Stat label="Récompense totale" value={`${detail.recompense_total.toFixed(0)} €`} icon={Euro} color="amber" />
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <p className="text-xs text-slate-500 mb-2">Statut récompense</p>
                  <Button
                    size="sm"
                    variant={detail.parrain.paye ? "default" : "outline"}
                    onClick={() => togglePaye(detail.parrain.id, detail.parrain.paye)}
                    className={detail.parrain.paye ? "bg-emerald-600 hover:bg-emerald-700 w-full" : "w-full"}
                    data-testid="toggle-paye-btn"
                  >
                    <BadgeCheck className="w-4 h-4 mr-1.5" />
                    {detail.parrain.paye ? "Payée" : "À payer"}
                  </Button>
                </div>
              </div>
              <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm text-indigo-700 font-mono truncate">{baseLinkUrl(detail.parrain.code)}</div>
                <a href={baseLinkUrl(detail.parrain.code)} target="_blank" rel="noopener noreferrer" className="text-indigo-600 text-xs hover:underline inline-flex items-center gap-1">
                  <ExternalLink className="w-3.5 h-3.5" /> Voir la page
                </a>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-emerald-600" />Filleuls ({detail.filleuls.length})</CardTitle></CardHeader>
              <CardContent>
                {detail.filleuls.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">Aucun filleul converti encore.</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {detail.filleuls.map((f) => (
                      <div key={f.livraison_id} className="py-2 flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{f.client_nom}</p>
                          <p className="text-xs text-slate-400">{f.date} · {f.statut}</p>
                        </div>
                        <p className="text-sm font-semibold text-emerald-600">{(f.prix || 0).toFixed(0)} €</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Eye className="w-4 h-4 text-indigo-600" />Historique des clics ({detail.clics.length})</CardTitle></CardHeader>
              <CardContent>
                {detail.clics.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">Personne n'a encore cliqué.</p>
                ) : (
                  <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                    {detail.clics.map((c) => (
                      <div key={c.id} className="py-2 text-xs text-slate-500">
                        <span className="font-medium text-slate-700">Clic</span> · {new Date(c.timestamp).toLocaleString("fr-FR")}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        // -------- List view --------
        <>
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Parrainages</h1>
            <p className="text-slate-500 mt-1">Suivez vos parrains, leurs filleuls et les récompenses à verser</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Parrains actifs" value={stats.leaderboard.length} icon={Users} color="indigo" />
            <Stat label="Clics totaux" value={stats.totals.clics} icon={LinkIcon} color="purple" />
            <Stat label="Filleuls convertis" value={stats.totals.filleuls} icon={TrendingUp} color="emerald" />
            <Stat label="CA généré" value={`${stats.totals.ca_genere.toFixed(0)} €`} icon={Euro} color="amber" />
            <Stat label="Récompenses à payer" value={`${stats.totals.recompense_due.toFixed(0)} €`} icon={Euro} color="red" />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between flex-wrap gap-2">
                <span className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" />Classement des parrains</span>
                <Input
                  placeholder="Rechercher nom, code, téléphone…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="max-w-xs"
                  data-testid="parrainage-search"
                />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredLeaderboard.length === 0 ? (
                <div className="py-8 text-center text-slate-400">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucun parrain trouvé</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredLeaderboard.map((p, i) => {
                    const RankIcon = i < 3 ? rankIcons[i] : null;
                    return (
                      <div key={p.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`parrain-row-${p.id}`}>
                        <div className="w-8 text-center">
                          {RankIcon ? <RankIcon className={`w-5 h-5 ${rankColors[i]}`} /> : <span className="text-sm text-slate-400">{i + 1}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <button onClick={() => setDetailId(p.id)} className="text-left hover:underline" data-testid={`parrain-name-${p.id}`}>
                            <p className="font-medium text-slate-800 truncate">
                              {p.nom}
                              {p.actif === false && (
                                <span className="ml-2 text-xs font-normal text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Désactivé</span>
                              )}
                            </p>
                          </button>
                          <p className="text-xs text-slate-400 font-mono truncate">{p.code}</p>
                        </div>
                        <div className="flex items-center gap-4 text-sm flex-wrap">
                          <div className="text-center">
                            <p className="text-slate-400 text-xs">Clics</p>
                            <p className="font-bold text-indigo-600">{p.clics}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-slate-400 text-xs">Filleuls</p>
                            <p className="font-bold text-emerald-600">{p.filleuls}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-slate-400 text-xs">Dû</p>
                            <p className={`font-bold ${p.recompense_due > 0 ? "text-amber-600" : "text-slate-400"}`}>{p.recompense_due.toFixed(0)}€</p>
                          </div>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="ghost" onClick={() => copyLink(p.code)} title="Copier le lien" data-testid={`copy-link-${p.id}`}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => shareLink(p)} title="Partager" data-testid={`share-link-${p.id}`}>
                            <Share2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant={p.paye ? "default" : "outline"} onClick={() => togglePaye(p.id, p.paye)} className={p.paye ? "bg-emerald-600 hover:bg-emerald-700" : ""} title="Marquer payé/dû" data-testid={`toggle-paye-${p.id}`}>
                            {p.paye ? <CheckCircle2 className="w-4 h-4" /> : "À payer"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => regenerate(p.id, p.nom)} title="Régénérer le code (invalide l'ancien)" className="text-slate-500 hover:text-indigo-600" data-testid={`regenerate-${p.id}`}>
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => toggleActive(p.id, p.actif !== false, p.nom)} title={p.actif === false ? "Réactiver le parrainage" : "Désactiver le parrainage"} className={p.actif === false ? "text-amber-500 hover:bg-amber-50" : "text-slate-500 hover:text-amber-600"} data-testid={`toggle-active-${p.id}`}>
                            {p.actif === false ? <Power className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteParrain(p.id, p.nom)} title="Supprimer le parrainage" className="text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-parrain-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, icon: Icon, color }) => (
  <div className={`bg-white rounded-xl border border-slate-200 p-4`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <Icon className={`w-4 h-4 text-${color}-500`} />
      <p className="text-xs text-slate-400">{label}</p>
    </div>
    <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
  </div>
);

export default Parrainages;
